// src/components/FinancialIntelligence/FIContextBar.js
//
// Shows current data state for the selected client:
// QB period, strategic narrative status, industry signals status.
// Includes inline QB refresh button (calls WorkspaceQuickBooksFinancials).

import React, { useState } from 'react';
import { Box, Typography, Button, Chip, CircularProgress, Tooltip } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { toast } from 'react-toastify';

const FIContextBar = ({ client, clientId, user }) => {
  const [refreshing, setRefreshing] = useState(false);

  if (!clientId || !client) {
    return (
      <Box sx={{ px: 3, py: 1, display: 'flex', alignItems: 'center', minHeight: 40, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.disabled">
          Select a client to see data context
        </Typography>
      </Box>
    );
  }

  // Derive data state from client doc
  const tier       = client.financialIntelligenceTier || 'none';
  const hasFullData = tier === 'full' || !!client.financialAnalysisSummary;
  const hasRanges  = !!client.financialRangesProvided;
  const qbConnected = !!client.quickbooksConnected;
  const qbPeriod   = client.quickbooksLastFetchedPeriod || null;
  const hasNarrative = !!client.masterSummary || !!client.clientStrategicNarrative;

  const handleRefreshQB = async () => {
    if (!client.quickbooksRealmId) {
      toast.warning('QuickBooks not connected for this client.');
      return;
    }

    setRefreshing(true);
    try {
      // WorkspaceQuickBooksFinancials only requires clientId — it reads realmId
      // from Firestore and defaults to the most recent period automatically.
      const fetchQBData = httpsCallable(functions, 'WorkspaceQuickBooksFinancials');
      await fetchQBData({ clientId });
      toast.success('QuickBooks data refreshed.');
    } catch (err) {
      console.error('[FIContextBar] QB refresh failed:', err);
      toast.error('QuickBooks refresh failed. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <Box sx={{
      px: 3,
      py: 0.8,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      flexWrap: 'wrap',
      bgcolor: 'grey.50',
      minHeight: 44,
    }}>

      {/* No data warning */}
      {!hasFullData && !hasRanges && (
        <Tooltip title="No financial data — connect QuickBooks or upload financials for full analysis">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningIcon sx={{ fontSize: 15, color: 'warning.main' }} />
            <Typography variant="caption" color="warning.dark" sx={{ fontSize: '0.75rem' }}>
              No financial data — analyses use strategic context only
            </Typography>
          </Box>
        </Tooltip>
      )}

      {/* Ranges-only warning */}
      {!hasFullData && hasRanges && (
        <Tooltip title="Connect QuickBooks or upload financials for full analysis">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningIcon sx={{ fontSize: 15, color: 'info.main' }} />
            <Typography variant="caption" color="info.dark" sx={{ fontSize: '0.75rem' }}>
              Using estimated financial ranges
            </Typography>
          </Box>
        </Tooltip>
      )}

      {/* QB period chip */}
      {hasFullData && qbPeriod && (
        <Chip
          icon={<CheckIcon sx={{ fontSize: '12px !important' }} />}
          label={`QuickBooks: ${qbPeriod}`}
          size="small"
          color="success"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.72rem' }}
        />
      )}

      {/* PDF financials chip — shown when Tier 2 but no QB connection */}
      {hasFullData && !qbPeriod && client?.financialSummarySource === 'pdf' && (
        <Chip
          icon={<CheckIcon sx={{ fontSize: '12px !important' }} />}
          label="Financials: PDF uploaded"
          size="small"
          color="success"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.72rem' }}
        />
      )}

      {/* Narrative chip */}
      {hasNarrative && (
        <Chip
          label="Narrative: current"
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.72rem', borderColor: 'grey.300', color: 'text.secondary' }}
        />
      )}

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* QB Refresh button — only when QB is connected */}
      {qbConnected && (
        <Button
          size="small"
          variant="text"
          startIcon={refreshing
            ? <CircularProgress size={12} color="inherit" />
            : <RefreshIcon sx={{ fontSize: 14 }} />
          }
          onClick={handleRefreshQB}
          disabled={refreshing}
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            textTransform: 'none',
            py: 0.3,
            '&:hover': { color: 'primary.main' },
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh QB Data'}
        </Button>
      )}
    </Box>
  );
};

export default FIContextBar;